/**
 * Single favorites network client.
 *
 * Built on Lovable's HMAC anon identity (`anonProof.ts` → x-anon-id / x-anon-proof
 * via edgeFn `anonAuth: true`). Callers never pass identity through query strings.
 *
 * Mutations still include `anon_user_id` in the JSON body for transitional backends
 * that read the body before / instead of headers.
 */
import type { Session } from '@supabase/supabase-js';
import { getAnonAuthHeaders } from '@/lib/anonProof';
import { edgeFn } from '@/lib/edgeFn';
import { getAnonUserId, isValidAnonUserId } from '@/lib/userIdentity';
import type { FerryFavorite } from '@/lib/favoritesStore';

export const ANON_ID_HEADER = 'x-anon-id';
export const ANON_PROOF_HEADER = 'x-anon-proof';

export interface FavoritesListResponse {
  favorites: FerryFavorite[];
}

export interface FavoritesMutateResponse {
  favorite: FerryFavorite;
}

export interface FavoritesDeleteResponse {
  deleted?: boolean;
}

export type FavoritesMergeSuccess = {
  ok: true;
  data: {
    favorites: FerryFavorite[];
    merged?: number;
    deduped?: number;
    claimed?: number;
    grandfather_limit?: number;
  };
};

export type FavoritesMergeFailure = {
  ok: false;
  error?: { code?: string; message?: string };
};

export type FavoritesMergeResult = FavoritesMergeSuccess | FavoritesMergeFailure;

export type CreateFavoriteInput = {
  samband_slug: string;
  custom_name?: string | null;
  position?: number;
  notify_departure_soon?: boolean;
  notify_delay?: boolean;
  notify_cancellation?: boolean;
  notify_time_to_leave?: boolean;
};

export type UpdateFavoriteInput = {
  id: string;
} & Partial<Omit<FerryFavorite, 'id' | 'samband_slug' | 'created_at' | 'updated_at'>>;

/**
 * When true, GET also appends ?anon_user_id= as a legacy fallback.
 * Default false — Lovable backend reads x-anon-id headers.
 */
export function isFavoritesLegacyQueryAnonEnabled(): boolean {
  const raw = import.meta.env.VITE_FAVORITES_LEGACY_QUERY_ANON;
  if (raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;
  return false;
}

function resolveAnonUserId(anonUserId?: string): string {
  const id = anonUserId ?? getAnonUserId();
  if (!isValidAnonUserId(id)) {
    throw new Error('[favoritesApi] invalid anon user id');
  }
  return id;
}

function identityBody(anonUserId: string): { anon_user_id: string } {
  return { anon_user_id: anonUserId };
}

function listQuery(anonUserId: string): Record<string, string> | undefined {
  if (!isFavoritesLegacyQueryAnonEnabled()) return undefined;
  return { anon_user_id: anonUserId };
}

export async function listFavorites(anonUserId?: string): Promise<FavoritesListResponse> {
  const id = resolveAnonUserId(anonUserId);
  const data = await edgeFn<FavoritesListResponse>('ferry-favorites', {
    method: 'GET',
    anonAuth: true,
    query: listQuery(id),
  });
  return {
    favorites: Array.isArray(data?.favorites) ? data.favorites : [],
  };
}

export async function createFavorite(
  input: CreateFavoriteInput,
  anonUserId?: string,
): Promise<FavoritesMutateResponse> {
  const id = resolveAnonUserId(anonUserId);
  return edgeFn<FavoritesMutateResponse>('ferry-favorites', {
    method: 'POST',
    anonAuth: true,
    body: {
      ...identityBody(id),
      ...input,
    },
  });
}

export async function updateFavorite(
  input: UpdateFavoriteInput,
  anonUserId?: string,
): Promise<FavoritesMutateResponse> {
  const id = resolveAnonUserId(anonUserId);
  return edgeFn<FavoritesMutateResponse>('ferry-favorites', {
    method: 'PATCH',
    anonAuth: true,
    body: {
      ...identityBody(id),
      ...input,
    },
  });
}

export async function deleteFavorite(
  sambandSlug: string,
  anonUserId?: string,
): Promise<FavoritesDeleteResponse> {
  const id = resolveAnonUserId(anonUserId);
  const data = await edgeFn<FavoritesDeleteResponse>('ferry-favorites', {
    method: 'DELETE',
    anonAuth: true,
    body: {
      ...identityBody(id),
      samband_slug: sambandSlug,
    },
  });
  return data ?? {};
}

/**
 * Authenticated merge of anon favorites into the signed-in user.
 * Headers come from getAnonAuthHeaders (x-anon-id + optional x-anon-proof).
 * Body keeps anon_user_id for transitional backends; op=merge stays in the query.
 */
export async function mergeFavorites(
  session: Session,
  anonUserId?: string,
  options?: { grandfatherCount?: number | null },
): Promise<FavoritesMergeResult> {
  const accessToken = session.access_token;
  if (!accessToken) {
    return { ok: false, error: { code: 'NO_TOKEN', message: 'Missing access token' } };
  }

  const id = resolveAnonUserId(anonUserId);
  const grandfatherCount = options?.grandfatherCount;
  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  const anonHeaders = await getAnonAuthHeaders();

  try {
    const res = await fetch(`${baseUrl}/functions/v1/ferry-favorites?op=merge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey,
        Authorization: `Bearer ${accessToken}`,
        ...anonHeaders,
      },
      body: JSON.stringify({
        ...identityBody(id),
        op: 'merge',
        ...(grandfatherCount != null ? { grandfather_count: grandfatherCount } : {}),
      }),
    });

    const parsed: unknown = await res.json().catch(() => null);
    if (!parsed || typeof parsed !== 'object' || !('ok' in parsed)) {
      return { ok: false, error: { message: `Invalid merge response (${res.status})` } };
    }

    return parsed as FavoritesMergeResult;
  } catch (err) {
    return {
      ok: false,
      error: { message: err instanceof Error ? err.message : 'Merge request failed' },
    };
  }
}
